import React, { type ReactNode, useContext, useEffect, useState } from 'react';

import { ArrowRight } from 'lucide-react';

import SentrySDK from 'common/3rd/SentrySDK';
import { reloadAllBoards } from 'common/actions/boards';
import { reloadCompany } from 'common/actions/company';
import AJAX from 'common/AJAX';
import RegistrationWrapper from 'common/canny/register/RegistrationWrapper';
import { CompanyContext } from 'common/containers/CompanyContainer';
import cloneElementWithProps from 'common/core/cloneElementWithProps';
import connect from 'common/core/connect';
import ErrorMessage from 'common/ErrorMessage';
import useMutation from 'common/hooks/useMutation';
import CompanyRoleNames from 'common/roles/CompanyRoleNames';
import Flex from 'common/ui/Flex';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';
import validateInput from 'common/validateInput';

import { CollectFeedbackOptionValue, CompanySizeValue, MotivationOptionValue } from './data';
import AdminV2ErrorView from '../v2/AdminV2ErrorView';

import type { BoardInput } from './AdminOnboardingCreateBoard';
import type { Company, UseCaseUpdateData } from 'common/api/endpoints/companies';
import type { SuccessResponse } from 'common/api/responses';

enum Step {
  What = 'what',
  How = 'how',
  Tools = 'tools',
  Team = 'team',
  InviteTeam = 'invite-team',
  Redirect = 'redirect',
}

type Location = {
  pathname: string;
};

type Props = {
  children: ReactNode;
  location: Location;
  reloadBoards: () => void;
  reloadCompany: () => void;
  router: {
    push: (path: string) => void;
    replace: (path: string) => void;
  };
};

const AdminOnboardingWrapper = ({ children, location, reloadCompany, router }: Props) => {
  const company = useContext<Company>(CompanyContext);

  const [board, setBoard] = useState<BoardInput>({
    name: 'Feature Requests',
    url: 'feature-requests',
  });
  const [error, setError] = useState<string | null>(null);
  const [inviteEmails, setInviteEmails] = useState<string[]>(Array(3).fill(''));
  const [skipping, setSkipping] = useState<boolean>(false);
  const [useCase, setUseCase] = useState<UseCaseUpdateData>({
    communicationTools: [],
    customAccess: false,
    feedbackSources: [],
    indexed: false,
    internalTools: [],
    marketingSources: [],
    motivations: [],
    otherFeedbackSource: null,
    otherMarketingSource: null,
    otherMotivation: null,
    otherTools: [],
    private: true,
    projectManagementTools: [],
    users: [],
    companySize: null,
    role: null,
    teams: [],
    ...company.useCase,
  });

  const skipInvite = useCase.companySize === CompanySizeValue.One;

  const updateUseCase = useMutation<SuccessResponse, UseCaseUpdateData>({
    mutationFn: (useCase) => AJAX.post('/api/company/updateUseCase', useCase),
    onSuccess: async (_, variables) => {
      const nextStep = whereIsNext(getCurrentStep()!);

      if (nextStep) {
        const path = `/admin/onboarding/${nextStep}`;
        router.push(path);
      } else {
        await reloadCompany();

        if (variables.motivations?.includes(CollectFeedbackOptionValue.Autopilot)) {
          router.push('/admin/autopilot');
          return;
        }
        router.push('/admin');
      }
    },
    onError: (error) => onRequestError(error),
  });

  // effects
  useEffect(() => {
    if (!company.viewerIsMember) {
      router.replace('/');
      return;
    }
  }, [company, router]);

  // helpers
  const canContinue = (currentStep: Step) => {
    switch (currentStep) {
      case Step.What:
        return useCase.motivations?.length > 0;
      case Step.How:
        return useCase.motivations.some((motivation) =>
          Object.values(CollectFeedbackOptionValue).includes(
            motivation as CollectFeedbackOptionValue,
          ),
        );
      case Step.Tools:
      case Step.Team:
      case Step.InviteTeam:
        return true;
      default:
        return false;
    }
  };

  const completeFlow = () =>
    updateUseCase.mutate({
      ...useCase,
      completed: true,
    });

  const getContinueText = (currentStep: Step) => {
    switch (currentStep) {
      case Step.InviteTeam:
        return 'Send Invites';
      default:
        return 'Continue';
    }
  };

  const getCurrentStep = () => {
    const { pathname } = location;
    const path = pathname.split('/');
    if (path.length !== 4 || path[2] !== 'onboarding') {
      return null;
    }

    return path[3] as Step;
  };

  const onBack = async () => {
    setError(null);

    const currentStep = getCurrentStep();
    if (!currentStep) {
      onUndefinedStep();
      return;
    }

    const backStep = whereIsBack(currentStep);
    const path = `/admin/onboarding/${backStep}`;
    router.push(path);
  };

  const onContinue = async () => {
    if (updateUseCase.isLoading) {
      return;
    }

    setError(null);

    const currentStep = getCurrentStep();
    if (!currentStep) {
      onUndefinedStep();
      return;
    }

    if (skipInvite && currentStep === Step.Team) {
      await completeFlow();
      return;
    }

    if (currentStep === Step.InviteTeam) {
      const sentInvites = await sendInvites();
      if (sentInvites) {
        await completeFlow();
      }
      return;
    }

    updateUseCase.mutate(useCase);
  };

  const onSkip = async () => {
    setError(null);

    const currentStep = getCurrentStep();
    if (!currentStep) {
      onUndefinedStep();
      return;
    }

    const nextStep = whereIsNext(currentStep);
    if (nextStep) {
      const path = `/admin/onboarding/${nextStep}`;
      router.push(path);
      return;
    }

    if (skipping) {
      return;
    }

    setSkipping(true);
    await completeFlow();
    setSkipping(false);
  };

  const onRequestError = async (requestError: { type: string; message: string }) => {
    SentrySDK.captureException(
      new Error(`Unhandled Signup Flow Error! ${requestError.type}, ${requestError.message}`),
    );
  };

  const onUndefinedStep = async () => {
    const { pathname } = location;
    SentrySDK.captureException(new Error(`Reached Undefined Flow Step, ${pathname}`));
  };

  const sendInvites = async (): Promise<boolean> => {
    const notEmpty = inviteEmails.filter((email) => email !== '');
    if (notEmpty.length === 0) {
      setError('Please provide an email to invite');
      return false;
    }

    const validEmails = inviteEmails.filter((email) => validateInput.email(email));
    if (notEmpty.length !== validEmails.length) {
      setError('Please ensure all emails are valid');
      return false;
    }

    const ownerRole = company.roles.find((role) => role.name === CompanyRoleNames.owner);
    const response = await AJAX.post('/api/company/inviteAdmins', {
      emails: notEmpty,
      roleID: ownerRole?._id,
    });
    const { error: requestError } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
    });

    if (requestError) {
      onRequestError(requestError);
    }

    return true;
  };

  const skipFeedback = !useCase.motivations?.some(
    (motivation) => motivation === MotivationOptionValue.Feedback,
  );

  const whereIsBack = (currentStep: Step) => {
    switch (currentStep) {
      case Step.How:
        return Step.What;
      case Step.Tools:
        return skipFeedback ? Step.What : Step.How;
      case Step.Team:
        return Step.Tools;
      case Step.InviteTeam:
        return Step.Team;
      default:
        return null;
    }
  };

  const whereIsNext = (currentStep: Step) => {
    switch (currentStep) {
      case Step.What:
        return skipFeedback ? Step.Tools : Step.How;
      case Step.How:
        return Step.Tools;
      case Step.Tools:
        return Step.Team;
      case Step.Team:
        return skipInvite ? null : Step.InviteTeam;
      default:
        return null;
    }
  };

  const getWrapperCn = (currentStep: Step) => {
    switch (currentStep) {
      case Step.What:
        return 'wrapper-slot:max-w-[700px]';
      case Step.InviteTeam:
        return 'wrapper-slot:max-w-[792px]';
      default:
        return 'wrapper-slot:max-w-[640px]';
    }
  };

  if (!company.viewerIsMember) {
    return <></>;
  }

  const currentStep = getCurrentStep();

  if (!currentStep) {
    return (
      <Flex className="items-center justify-center w-screen h-screen">
        <AdminV2ErrorView />
      </Flex>
    );
  }

  const backStep = whereIsBack(currentStep);
  const nextStep = whereIsNext(currentStep);
  const canSkip = [Step.Tools, Step.Team, Step.InviteTeam].includes(currentStep);
  const continueText = getContinueText(currentStep);

  const errorMsg = error || updateUseCase.error?.message;

  return (
    <RegistrationWrapper className={getWrapperCn(currentStep)}>
      {cloneElementWithProps(children, {
        board,
        inviteEmails,
        onChange: setUseCase,
        onChangeBoard: setBoard,
        onChangeInviteEmails: setInviteEmails,
        onSubmit: onContinue,
        useCase,
      })}
      <RegistrationWrapper.Navigation
        back={{
          onClick: onBack,
          text: 'Back',
          visible: !!backStep,
        }}
        continue={{
          onClick: onContinue,
          text: continueText,
          disabled: !canContinue(currentStep),
          endIcon: nextStep ? ArrowRight : undefined,
        }}
        skip={{
          onClick: onSkip,
          text: 'Skip',
          visible: canSkip,
        }}
        isLoading={updateUseCase.isLoading}
      />
      {errorMsg && <ErrorMessage size="large">{errorMsg}</ErrorMessage>}
    </RegistrationWrapper>
  );
};

export default connect(null, { reloadBoards: reloadAllBoards, reloadCompany })(
  AdminOnboardingWrapper,
);
